import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';

// Data
const education = [
  {
    period: '2016 - 2019',
    institution: 'SMAN 6 Bekasi',
    company: 'IPA',
    description: 'Graduated from SMAN 6 Bekasi focus on science.',
  },
  {
    period: '2019 - 2024',
    institution: 'Universitas Dian Nuswantoro',
    company: 'Informatics Engineering',
    description: (
      <>
        <p>Bachelor Degree in Informatics Engineering from Universitas Dian Nuswantoro.</p>
        <p>GPA: 3.35/4.00</p>
      </>
    ),
  },
];

const workExperience = [
  {
    period: 'Mar 2022 - Jun 2022',
    position: 'Fullstack Developer',
    company: 'CV. Rajawali Merah',
    description:
      'Developed a web-based e-commerce system including admin dashboard and user pages using PHP, JavaScript, MySQL, Bootstrap with an integrated payment gateway system.',
  },
  {
    period: 'Jan 2025 - Present',
    position: 'Fullstack Web Developer Bootcamp',
    company: 'Udemy',
    description:
      'Currently taking a Web Developer Bootcamp about Fullstack Development using HTML5, CSS3, JavaScript, React.js, Node.js, Express.js, and MongoDB.',
  },
];

// Animation
const boxVariants = {
  hidden: { opacity: 0, y: 40, scale: 0.95 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.6, ease: 'easeOut' },
  },
};

// Timeline item
const TimelineItem = ({ period, title, subtitle, description }) => (
  <div className="relative pl-10 pb-12">
    <div className="absolute left-0 top-2 w-4 h-4 rounded-full border-2 border-[#dbcfc1] bg-[#a98467] z-10"></div>
    <div className="pl-2">
      <p className="text-md font-semibold text-[#5e4b45]">{period}</p>
      <h3 className="text-lg font-bold text-[#3b2f2f]">{title}</h3>
      {subtitle && <p className="italic text-[#8b6d59]">{subtitle}</p>}
      <div className="text-[#3b2f2f] text-justify text-md">{description}</div>
    </div>
  </div>
);

// Timeline container
const Timeline = ({ data, type }) => (
  <div className="relative border-l-2 border-[#dbcfc1] ml-2">
    {data.map((item, idx) => (
      <TimelineItem
        key={idx}
        period={item.period}
        title={type === 'edu' ? item.institution : item.position}
        subtitle={type === '' ? '' : item.company}
        description={item.description}
      />
    ))}
  </div>
);

// Main component
const Experiences = () => {
  const eduRef = useRef(null);
  const workRef = useRef(null);

  const isEduInView = useInView(eduRef, { once: true, amount: 0.4 });
  const isWorkInView = useInView(workRef, { once: true, amount: 0.4 });

  return (
    <section
      id="experiences"
      className="min-h-screen py-20 px-6 md:px-20 bg-[#F5F3EF] text-[#3b2f2f] flex flex-col md:flex-col lg:flex-col xl:flex-row justify-center items-stretch gap-10"
    >
      <div className="w-full max-w-[1440px] flex flex-col xl:flex-row items-stretch gap-10">
        {/* Education */}
        <motion.div
          ref={eduRef}
          className="w-full md:w-100 md:h-fit bg-white/60 border border-[#dbcfc1] rounded-xl p-8 shadow-md"
          variants={boxVariants}
          initial="hidden"
          animate={isEduInView ? 'visible' : 'hidden'}
          whileHover={{ scale: 1.02 }}
          transition={{ type: 'spring', stiffness: 200 }}
          style={{ minHeight: '680px' }}
        >
          <h2 className="text-2xl md:text-2xl font-bold mb-6 text-[#5e4b45] text-center md:text-left">Education</h2>
          <Timeline data={education} type="edu" />
        </motion.div>

        {/* Work Experience */}
        <motion.div
          ref={workRef}
          className="w-full md:w-100 md:h-fit bg-white/60 border border-[#dbcfc1] rounded-xl p-8 shadow-md"
          variants={boxVariants}
          initial="hidden"
          animate={isWorkInView ? 'visible' : 'hidden'}
          whileHover={{ scale: 1.02 }}
          transition={{ type: 'spring', stiffness: 200 }}
          style={{ minHeight: '680px' }}
        >
          <h2 className="text-2xl md:text-2xl font-bold mb-6 text-[#5e4b45] text-center md:text-left">Work Experience & Courses</h2>
          <Timeline data={workExperience} type="work" />
        </motion.div>
      </div>
    </section>
  );
};

export default Experiences;
